import { keepPreviousData } from '@tanstack/react-query';
import { useAppQuery } from 'core/query/hooks/useAppQuery';
import { activityLogQueryKeys } from '../activityLogQueryKeys';
import { activityLogApi } from '../../api/activityLogApi';
import { ActivityLogQueryParams, ActivityLogResponse } from '../../types/activityLog';

/**
 * React Query hook for fetching activity logs
 *
 * @param params - Query parameters for filtering, pagination, and sorting
 * @returns Activity logs data and query state
 */
export const useActivityLogsQuery = (params: ActivityLogQueryParams & { enabled?: boolean }) => {
  const { enabled = true, ...rest } = params;

  const { data: queryData, ...query } = useAppQuery<{
    data: ActivityLogResponse;
  }>({
    queryKey: activityLogQueryKeys.list({
      identifier: `useActivityLogsQuery`,
      ...rest,
    }),
    queryFn: async ({ token, buildConfig }) => {
      // buildConfig() automatically includes URL context headers (tenant/realmId)
      const response = await activityLogApi({ params: rest, token, config: buildConfig() });
      return { data: response.data as ActivityLogResponse };
    },
    staleTime: 30000, // 30 seconds - prevents aggressive refetching
    gcTime: 5 * 60 * 1000, // 5 minutes - keep data in cache longer
    refetchOnWindowFocus: false,
    refetchOnMount: false, // Don't refetch on mount if data exists
    placeholderData: keepPreviousData, // Keep previous data while fetching new data - prevents flash
    enabled,
  });

  return { activityLogs: queryData?.data, ...query };
};

export default useActivityLogsQuery;
